import { useVirtualizer } from '@tanstack/react-virtual'
import { type CSSProperties, type Key, type ReactNode, useEffect, useRef } from 'react'
import UiEmpty from '../ui-empty'
import { PhantomSkeleton } from '../ui-skeleton'
import './UiVirtualList.scss'

export interface UiVirtualListProps<R> {
  data: readonly R[]
  rowKey: (row: R) => Key
  renderItem: (row: R, index: number) => ReactNode
  loading?: boolean
  /** Còn trang kế (cursor) → cuộn gần cuối sẽ gọi `onEndReached`. */
  hasMore?: boolean
  isFetchingMore?: boolean
  onEndReached?: () => void
  /** Chiều cao tối đa của vùng cuộn (mặc định co theo viewport). */
  maxHeight?: CSSProperties['maxHeight']
  /** Chiều cao ước lượng một mục (px); chiều cao thật được đo lại bằng `measureElement`. */
  estimateSize?: number
  emptyText?: string
  testId?: string
}

const END_REACHED_THRESHOLD = 6
const INITIAL_VIEWPORT_HEIGHT = 600

/**
 * Danh sách ảo hoá bằng TanStack Virtual cho mục cao không đều: chỉ dựng các mục đang nhìn thấy,
 * đo chiều cao thật từng mục, cuộn vô hạn theo cursor.
 */
const UiVirtualList = <R extends object>({
  data,
  rowKey,
  renderItem,
  loading,
  hasMore,
  isFetchingMore,
  onEndReached,
  maxHeight = 'max(320px, calc(100dvh - 340px))',
  estimateSize = 72,
  emptyText = 'Không có dữ liệu',
  testId,
}: Readonly<UiVirtualListProps<R>>) => {
  const scrollRef = useRef<HTMLDivElement>(null)

  const virtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimateSize,
    overscan: 8,
    // Khung ước lượng cho lần dựng đầu, trước khi đo được vùng cuộn thật → không nháy danh sách trống.
    initialRect: { width: 0, height: INITIAL_VIEWPORT_HEIGHT },
  })

  const items = virtualizer.getVirtualItems()
  const lastIndex = items.length > 0 ? items[items.length - 1].index : -1

  // biome-ignore lint/correctness/useExhaustiveDependencies: chỉ chạy lại khi vị trí cuối hoặc số mục đổi
  useEffect(() => {
    if (!hasMore || isFetchingMore || !onEndReached) return
    if (lastIndex >= data.length - 1 - END_REACHED_THRESHOLD) onEndReached()
  }, [lastIndex, data.length, hasMore, isFetchingMore])

  return (
    <div className="ui-vl" data-testid={testId}>
      <div ref={scrollRef} className="ui-vl-scroll" style={{ maxHeight }}>
        {data.length === 0 && !loading ? (
          <div className="ui-vl-empty">
            <UiEmpty description={emptyText} />
          </div>
        ) : (
          <div className="ui-vl-body" style={{ height: virtualizer.getTotalSize() }}>
            {items.map((virtualItem) => {
              const row = data[virtualItem.index]
              return (
                <div
                  key={rowKey(row)}
                  ref={virtualizer.measureElement}
                  data-index={virtualItem.index}
                  className="ui-vl-item"
                  style={{ transform: `translateY(${virtualItem.start}px)` }}
                >
                  {renderItem(row, virtualItem.index)}
                </div>
              )
            })}
          </div>
        )}

        {(loading || isFetchingMore) && (
          <div className="ui-vl-loading">
            <PhantomSkeleton kind="table" />
          </div>
        )}
      </div>
    </div>
  )
}

export default UiVirtualList
